import React, { createContext, useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import { clientErrorHandler } from '@sm/webassets';
import { useLocation } from 'react-router-dom';
import { SearchContext } from '../../../../components/SearchContext';
import appsAppListings from './AppListings.graphql';
import AppListingSkeleton from './AppListingSkeleton';
import { POPULAR } from '~app/pages/AppsDirectory/components/SearchContext/constants';

const initialState = {
  search: '',
  categories: [],
  featured: false,
  sortBy: POPULAR.value,
  page: 1,
  pageSize: 16,
};

const isNewParamsInitialState = newVariables => {
  const paramsWithoutSort = { ...newVariables, sortBy: '', sortOrder: '' };
  const initialWithoutSort = { ...initialState, sortBy: '', sortOrder: '' };
  return JSON.stringify(paramsWithoutSort) === JSON.stringify(initialWithoutSort);
};

export const AppListingContext = createContext({
  appListings: [],
  isInitialSearch: false,
  pageInfo: {},
});

export const AppListingProvider = ({ children }) => {
  const { state } = useLocation();

  const { search, filters, sortBy, sortOrder, pageNum, resetSearch } = useContext(SearchContext);
  const [appListings, setAppListings] = useState([]);
  const [isInitialSearch, setInitialSearch] = useState(true);
  const [pageInfo, setPageInfo] = useState({});
  const [variables, setVariables] = useState(initialState);

  const {
    loading: isLoading,
    error,
    data,
  } = useQuery(appsAppListings, {
    variables,
  });

  useEffect(() => {
    if (data?.fetchPublishedApplicationListings?.data) {
      const { data: listings, ...paging } = data.fetchPublishedApplicationListings;
      setPageInfo(paging);
      setAppListings(listings);
    }
  }, [data]);

  useEffect(() => {
    if (state) {
      const { moreParams = {} } = state;
      if (Object.keys(moreParams).length) {
        setVariables(prevVariables => ({
          ...prevVariables,
          ...moreParams,
        }));
      }
    }
  }, [state]);

  useEffect(() => {
    if (error) {
      clientErrorHandler.logError(error);
    }
  }, [error]);

  useEffect(() => {
    setVariables(prevVariables => {
      if (prevVariables.search !== search) {
        const newVariables = { ...prevVariables, search };
        setInitialSearch(isNewParamsInitialState(newVariables));
        return newVariables;
      }
      return prevVariables;
    });
  }, [search]);

  useEffect(() => {
    setVariables(prevVariables => {
      const newVariables =
        filters && filters.length > 0
          ? { ...prevVariables, categories: [filters.toString()] }
          : { ...prevVariables, categories: [] };
      setInitialSearch(isNewParamsInitialState(newVariables));
      return newVariables;
    });
  }, [filters]);

  useEffect(() => {
    setVariables(prevVariables => {
      if (prevVariables.sortBy !== sortBy) {
        const newVariables = { ...prevVariables, sortBy };
        setInitialSearch(isNewParamsInitialState(newVariables));
        return newVariables;
      }
      return prevVariables;
    });
  }, [sortBy]);

  useEffect(() => {
    setVariables(prevVariables => {
      if (prevVariables.sortOrder !== sortOrder) {
        const newVariables = { ...prevVariables, sortOrder };
        setInitialSearch(isNewParamsInitialState(newVariables));
        return newVariables;
      }
      return prevVariables;
    });
  }, [sortOrder]);

  useEffect(() => {
    setVariables(prevVariables => {
      const nextPageNum = pageNum + 1;
      if (prevVariables.page !== nextPageNum) {
        const newVariables = { ...prevVariables, page: nextPageNum };
        setInitialSearch(isNewParamsInitialState(newVariables));
        return newVariables;
      }
      return prevVariables;
    });
  }, [pageNum]);

  const clearSearch = () => {
    resetSearch();
    setVariables(initialState);
    setInitialSearch(true);
  };

  if (isLoading) {
    return <AppListingSkeleton data-testid="AppListingContext__Skeleton" />;
  }

  return (
    <AppListingContext.Provider value={{ appListings, isInitialSearch, pageInfo, isLoading, clearSearch }}>
      {children}
    </AppListingContext.Provider>
  );
};

AppListingProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AppListingContext;
